import { useContext } from 'react';
import { DataContext } from '@visx/xychart';

import styles from './useCrosshair.module.css';

interface UseCrosshairOptions {
  /**
   * The position (in SVG coordinates) where the crosshair should appear.
   * For vertical crosshairs, this is the X position. For horizontal, this is the Y position.
   */
  position: number | null;
  /**
   * The orientation of the crosshair line.
   * @default 'vertical'
   */
  orientation?: 'vertical' | 'horizontal';
}

/**
 * Hook that renders a crosshair line at the given position.
 *
 * @param options - Configuration for the crosshair (position and orientation)
 * @returns JSX for the crosshair SVG element, or null if no position provided
 */
export function useCrosshair({ position, orientation = 'vertical' }: UseCrosshairOptions): React.ReactNode {
  const dataContext = useContext(DataContext);

  if (position === null || !dataContext?.yScale || !dataContext?.xScale) {
    return null;
  }

  if (orientation === 'vertical') {
    // Get the y-scale range to determine the chart area bounds
    const yRange = dataContext.yScale.range();
    const yMin = Math.min(...yRange.map((v) => (typeof v === 'number' ? v : 0)));
    const yMax = Math.max(...yRange.map((v) => (typeof v === 'number' ? v : 0)));

    return (
      <svg className={styles.crosshairContainer}>
        <line
          x1={position}
          y1={yMin}
          x2={position}
          y2={yMax}
          stroke="var(--lp-color-text-ui-primary-base)"
          strokeWidth={1}
          className={styles.crosshairLine}
        />
      </svg>
    );
  }

  // Horizontal crosshair
  const xRange = dataContext.xScale.range();
  const xMin = Math.min(...xRange.map((v) => (typeof v === 'number' ? v : 0)));
  const xMax = Math.max(...xRange.map((v) => (typeof v === 'number' ? v : 0)));

  return (
    <svg className={styles.crosshairContainer}>
      <line
        x1={xMin}
        y1={position}
        x2={xMax}
        y2={position}
        stroke="var(--lp-color-text-ui-primary-base)"
        strokeWidth={1}
        className={styles.crosshairLine}
      />
    </svg>
  );
}
